import React from 'react';

// Difficulty is metadata teachers/parents use to gauge worksheet calibration.
// Students should answer without being primed by it, so we gate visibility by
// role. Guests (no user) also see nothing — keeps the experience identical to
// students who follow guest links.
const ALLOWED_ROLES = new Set([
  'parent',
  'coaching_teacher',
  'school_teacher',
  'school_admin',
  'super_admin',
  'qb_admin',
]);

export const canUserSeeQuestionDifficulty = (user) =>
  Boolean(user && ALLOWED_ROLES.has(user.role));

const normalizeLevel = (raw) => {
  if (raw === null || raw === undefined) return '';
  const value = String(raw).trim().toLowerCase();
  if (!value) return '';
  if (value.startsWith('eas')) return 'easy';
  if (value.startsWith('med')) return 'medium';
  if (value.startsWith('har')) return 'hard';
  if (value.startsWith('ext')) return 'extreme';
  return value;
};

const LEVEL_STYLES = {
  easy: { label: 'Easy', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  medium: { label: 'Medium', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  hard: { label: 'Hard', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  extreme: { label: 'Extreme', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
};

export const DifficultyBadge = ({ difficulty, className = '' }) => {
  const level = normalizeLevel(difficulty);
  if (!level) return null;
  const styles = LEVEL_STYLES[level] || {
    label: String(difficulty).trim() || 'N/A',
    cls: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border ${styles.cls} ${className}`}
      aria-label={`Difficulty: ${styles.label}`}
      title={`Difficulty: ${styles.label}`}
    >
      {styles.label}
    </span>
  );
};

const HTML_CLS_BY_LEVEL = {
  easy: 'qdiff-easy',
  medium: 'qdiff-medium',
  hard: 'qdiff-hard',
  extreme: 'qdiff-extreme',
};

const HTML_LABEL_BY_LEVEL = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  extreme: 'Extreme',
};

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));

export const renderDifficultyBadgeHtml = (difficulty) => {
  const level = normalizeLevel(difficulty);
  if (!level) return '';
  const cls = HTML_CLS_BY_LEVEL[level] || 'qdiff-unknown';
  const label = HTML_LABEL_BY_LEVEL[level] || escapeHtml(String(difficulty).trim());
  return `<span class="q-difficulty-badge ${cls}">${label}</span>`;
};

// CSS rules for the HTML-embedded badge. Kept here so callers that inject the
// badge into raw worksheet HTML can append the same styles in their <style>
// block and stay visually consistent with the JSX badge above.
export const DIFFICULTY_BADGE_HTML_STYLES = `
.q-difficulty-badge {
  display: inline-block;
  padding: 2px 8px;
  margin-left: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 4px;
  border: 1px solid;
  vertical-align: middle;
  font-family: Arial, sans-serif;
  line-height: 1.4;
}
.q-difficulty-badge.qdiff-easy { background: #ecfdf5; color: #047857; border-color: #6ee7b7; }
.q-difficulty-badge.qdiff-medium { background: #fffbeb; color: #b45309; border-color: #fcd34d; }
.q-difficulty-badge.qdiff-hard { background: #fff1f2; color: #be123c; border-color: #fecdd3; }
.q-difficulty-badge.qdiff-extreme { background: #f5f3ff; color: #6d28d9; border-color: #c4b5fd; }
.q-difficulty-badge.qdiff-unknown { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
`;

// Inject a difficulty badge inside each `<div class="question" data-question-id="N">`
// in the server-rendered worksheet HTML. The badge is appended right after the
// opening wrapper tag so it appears at the top of the question card. Questions
// whose number isn't in `difficultyByQuestionNumber` are left untouched, so
// legacy worksheets without per-question difficulty data still render cleanly.
export const injectDifficultyBadgesIntoHtml = (html, difficultyByQuestionNumber) => {
  if (!html || typeof html !== 'string') return html;
  if (!difficultyByQuestionNumber || typeof difficultyByQuestionNumber !== 'object') {
    return html;
  }
  return html.replace(
    /<div\s+class="question"\s+data-question-id="([^"]+)"[^>]*>/gi,
    (match, qid) => {
      const difficulty =
        difficultyByQuestionNumber[String(qid)] ?? difficultyByQuestionNumber[qid];
      const badge = renderDifficultyBadgeHtml(difficulty);
      return badge ? `${match}${badge}` : match;
    },
  );
};
